import {generateGuid} from '@packages/utilities/guid';
import {
  getCurrentOrDefaultExpression,
  getFieldDefaultExpression,
} from '@packages/systems/core/component-conversions';
import {getSecond} from '@packages/utilities/Pair';
import {
  type Type,
  withSystem,
  Plugins,
  Component,
  Types,
  Expressions,
  Variable,
  ExpressionPath,
  matchExpression,
  ExpressionPathPart,
} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {Expression, EList, ECall, EVariable} from '@packages/systems/wfdl-ast';
import {
  constant,
  constantFalse,
  flip,
  identity,
} from '@packages/utilities/fp/utils';
import {findLastIndex} from 'lodash';
import {reportBug} from '@packages/utilities/reporter';
import {isDynamoWrapper} from '@packages/utilities/elementTypeUtils';
import {isConditional} from '@packages/systems/plugins-utils/Conditionals';
import {deconstructAppend} from '@packages/utilities/append';

const isReferenceToField = flip(
  matchExpression<(arg1: string) => boolean>({
    Select:
      ({from, prop}) =>
      (fieldName: string) => {
        if (prop !== fieldName) return false;
        return Expressions.isDataVariable(from);
      },
    _: constant(constantFalse),
  })
);

export function findAllReferencesToField(
  expression: Expression,
  fieldName: string
): Array<ExpressionPath> {
  return Expressions.findPaths(expression, isReferenceToField(fieldName));
}

const unbindExpressionFieldAtCallRoot = (
  rootExpr: Expression,
  expressionPath: ExpressionPath,
  newExpr: Expression,
  plugins: Plugins.PluginsType,
  componentRender: Expression,
  isAttributesField: boolean
): Expression => {
  // In order to not duplicate the default expression to every instance
  // of the override field we generate new IDs for all elements
  const uniqueExpression = getSecond(
    instantiateExpressionWithUniqueIds(newExpr)(generateGuid())
  );
  /**
   * The `expressionPath` _up until_ it encounters a `Call`.
   *
   * This means we will unbind at the outermost _call site_ rather than the
   * deepest _arg site_. If we unbound at the _arg site_, we would need to
   * replace the argument with a `Hole`, or some default value.
   *
   * Instead, we replace the data with an evaluated form of our expression under
   * the current environment - effectively "stamping" the value in place.
   */
  for (let index = expressionPath.length - 1; index >= 0; index--) {
    const pathPart = expressionPath[index];

    if (!pathPart) {
      break;
    }

    if (pathPart.in === 'Select') {
      // handle deeply nested selects
      continue;
    }

    if (pathPart.in === 'Call') {
      // this is the innermost call wrapping the component prop binding which
      // should represent the implicit function call wrapping the binding if
      // there is one
      const path = expressionPath.slice(0, index);

      // A Basic::Attributes field bound as `List::Append(staticList)(data.field)`
      // is a Call, but not an implicit function — the check below would fall
      // through and stamp the default value at the arg site, leaving the
      // `Append` call dangling around it. Collapse it into a flat static list
      // instead, merging the field's default value into the static segment.
      if (isAttributesField) {
        const callExpr = Expressions.getIn(rootExpr, path);
        const appendParts = callExpr ? deconstructAppend(callExpr) : [];
        if (appendParts.length > 1) {
          const staticSegment = appendParts[0];
          const staticItems = staticSegment
            ? (Expressions.getList(staticSegment) ?? [])
            : [];
          // uniqueExpression here always came from getCurrentOrDefaultExpression
          // for a Basic::Attributes field (guarded by isAttributesField above),
          // whose alias always resolves to a TList — its List-materializing
          // branch (defaultList) unconditionally returns an EList, even when
          // the configured defaultValue is missing or malformed, so this can
          // never actually fall through to []; the fallback is just defensive.
          const defaultItems = Expressions.getList(uniqueExpression) ?? [];
          return Expressions.setIn(
            rootExpr,
            path,
            EList([...staticItems, ...defaultItems])
          );
        }
      }

      const retType = getImplicitFunctionReturnType(rootExpr, path, plugins);
      if (retType) {
        return replaceImplicitFunctionValue(
          rootExpr,
          retType,
          plugins,
          uniqueExpression,
          expressionPath,
          path
        );
      }
    }

    break;
  }

  const shouldUnwrapDistinct = isLastElementDynamoWrapper(
    expressionPath,
    componentRender
  );

  // If the path is to a dynamo wrapper we need to unwrap the expression
  // otherwise keep the Expression as is
  const unWrappedExpression = shouldUnwrapDistinct
    ? matchExpression({
        Distinct: ({value}) => value,
        _: identity,
      })(uniqueExpression)
    : uniqueExpression;

  return Expressions.setIn(rootExpr, expressionPath, unWrappedExpression);
};

function isLastElementDynamoWrapper(
  path: ExpressionPath,
  componentRender: Expression
): boolean {
  const lastElementIndex = findLastIndex(
    path,
    (part: ExpressionPathPart) => part.in === 'Element'
  );
  if (lastElementIndex === -1) {
    return false;
  }
  const slicedPath = path.slice(0, lastElementIndex);
  const exprAtPath = Expressions.getIn(componentRender, slicedPath);
  if (!exprAtPath) {
    return false;
  }
  const elementAtPath = Expressions.getElement(exprAtPath);
  if (!elementAtPath) {
    return false;
  }
  return isDynamoWrapper(elementAtPath);
}

function getImplicitFunctionReturnType(
  rootExpr: Expression,
  unbindPath: ExpressionPath,
  plugins: Plugins.PluginsType
): Type | undefined {
  const exprAtPath = Expressions.getIn(rootExpr, unbindPath);
  if (!exprAtPath) {
    return undefined;
  }
  const deconstructedCall = Expressions.deconstructCall(exprAtPath);
  const variable = Expressions.getVariable(deconstructedCall.fun);
  if (!variable) {
    return undefined;
  }
  const functionType = Variable.match({
    qualified: (v) => plugins.values.get(v),
    unqualified: () => undefined,
  })(variable);
  if (!functionType || functionType.envType.implicit === 'no') {
    return undefined;
  }
  const deconstructedFunctionType = Types.deconstructFunctionType(
    functionType.envType.dataType
  );
  return deconstructedFunctionType.ret;
}

/**
 * Evaluates an implicit function expression so that we get correct expression to stamp in place of the deleted prop
 */
const replaceImplicitFunctionValue = (
  root: Expression,
  targetType: Type,
  plugins: Plugins.PluginsType,
  replacedProp: Expression,
  expressionPath: ExpressionPath,
  unbindPath: ExpressionPath
): Expression => {
  const rootWithReplacedProp = Expressions.setIn(
    root,
    expressionPath,
    replacedProp
  );
  const functionExprToEvaluate = Expressions.getIn(
    rootWithReplacedProp,
    unbindPath
  );
  let evaluatedValue = null;
  try {
    evaluatedValue = functionExprToEvaluate
      ? Expressions.evaluate(
          Plugins.getEvalEnv(plugins),
          functionExprToEvaluate
        )
      : null;
  } catch (e) {
    reportEvaluationBug(functionExprToEvaluate);
  }
  if (evaluatedValue === null) {
    reportEvaluationBug(functionExprToEvaluate);
  }
  const newExpr = getCurrentOrDefaultExpression(plugins)(
    withSystem({defaultValue: evaluatedValue}, targetType)
  );
  const uniqueExpression = getSecond(
    instantiateExpressionWithUniqueIds(newExpr)(generateGuid())
  );
  return Expressions.setIn(root, unbindPath, uniqueExpression);
};

const getExpressionValueToReplace = (
  component: Component.ComponentType,
  fieldId: string,
  plugins: Plugins.PluginsType
): Expression | null => {
  const componentRender = Component.getRender(component);
  const rootElement = Expressions.getElement(componentRender);
  if (!rootElement) {
    // The component render root is expected to be an element here
    return null;
  }

  const rootDataType = Component.getDataType(component);
  const rootDataTypeFields = Types.getRecordFields(rootDataType) || {};
  const unbindDataType = rootDataTypeFields[fieldId];
  if (!unbindDataType) {
    throw new TypeError(
      `Unable to find field from Type: ${Types.print(
        rootDataType
      )}, fieldId: ${fieldId}`
    );
  }

  return getFieldDefaultExpression(component, fieldId, unbindDataType, plugins);
};

export function unbindReferences(
  expr: Expression,
  fieldId: string,
  component: Component.ComponentType,
  plugins: Plugins.PluginsType
): Expression {
  // Create a new Expression based on the defaultValue of the field we will
  // be deleting
  const replaceExpr = getExpressionValueToReplace(component, fieldId, plugins);

  if (!replaceExpr) {
    return expr;
  }

  // Basic::Attributes fields can be bound as `List::Append(staticList)(data.field)`.
  // Only those fields get the Append-collapsing treatment in
  // `unbindExpressionFieldAtCallRoot` below — every other field type keeps
  // today's arg-site substitution behavior unchanged.
  const fieldType = Types.getRecordFields(Component.getDataType(component))?.[
    fieldId
  ];
  const isAttributesField = Boolean(
    fieldType && Types.isAttributesType(fieldType)
  );

  const conditionalPaths = Expressions.findPaths(expr, isConditional);

  if (conditionalPaths.length > 0) {
    expr = conditionalPaths.reduce((updatedExpr, conditionalPath) => {
      return unbindReferencesFromConditionSources(
        updatedExpr,
        fieldId,
        conditionalPath,
        component,
        plugins
      );
    }, expr);
  }

  const referencePaths = findAllReferencesToField(expr, fieldId);

  if (referencePaths.length > 0) {
    return referencePaths.reduce((accExpr, exprPath) => {
      return unbindExpressionFieldAtCallRoot(
        accExpr,
        exprPath,
        replaceExpr,
        plugins,
        expr,
        isAttributesField
      );
    }, expr);
  }
  // If we were unable to find references or create a replaceable Expression,
  // return back the original root expr
  return expr;
}

/**
 * Removes bindings to `data.fieldId` used as condition sources in the
 * conditional found at `conditionalPath` in `expr`.
 *
 * Removes all conditions that use `data.fieldId` as the condition source.
 * If this results in empty condition groups, remove the empty groups.
 * If this results in a conditional with no groups, replace the conditional
 * with its fallback value.
 */
function unbindReferencesFromConditionSources(
  expr: Expression,
  fieldId: string,
  conditionalPath: ExpressionPath,
  component: Component.ComponentType,
  plugins: Plugins.PluginsType
): Expression {
  const conditionalExpr = Expressions.getIn(expr, conditionalPath);
  const deconstructedConditional = conditionalExpr
    ? deconstructConditionalForUnbinding(conditionalExpr)
    : null;

  if (!deconstructedConditional) {
    return expr;
  }

  const isReferenceToTargetField = isReferenceToField(fieldId);

  const {sourceFieldsByConditionAndGroup, fallbackValue} =
    deconstructedConditional;
  const numGroups = sourceFieldsByConditionAndGroup.length;

  const groupsToRemove: number[] = [];

  for (let group = 0; group < numGroups; group++) {
    const sourceFieldsByCondition = sourceFieldsByConditionAndGroup[group];
    const conditionsToRemove: number[] = [];

    // find all conditions in the group that use the target field as source
    if (sourceFieldsByCondition) {
      const numConditions = sourceFieldsByCondition.length;
      for (let condition = 0; condition < numConditions; condition++) {
        const sourceField = sourceFieldsByCondition[condition];
        if (sourceField && isReferenceToTargetField(sourceField)) {
          // tag the condition for removal
          conditionsToRemove.push(condition);
        }
      }
    }

    const pathToGroupConditions: ExpressionPath = [
      ...conditionalPath,
      {in: 'Call', at: 'arg'},
      {in: 'Record', at: 'rules'},
      {in: 'List', index: group},
      {in: 'Record', at: 'condition'},
      {in: 'Call', at: 'arg'},
    ];

    const groupConditionsExpr = Expressions.getIn(expr, pathToGroupConditions);
    const groupConditions = groupConditionsExpr
      ? Expressions.getList(groupConditionsExpr)
      : null;

    if (groupConditions) {
      // remove the conditions tagged for removal from the group
      const updatedGroupConditions = groupConditions.filter(
        (_, index) => !conditionsToRemove.includes(index)
      );

      if (updatedGroupConditions.length === 0) {
        // if all conditions in a group are removed, tag the group for removal
        groupsToRemove.push(group);
      } else {
        expr = Expressions.setIn(
          expr,
          pathToGroupConditions,
          EList(updatedGroupConditions)
        );
      }
    }
  }

  if (groupsToRemove.length > 0) {
    const pathToGroups: ExpressionPath = [
      ...conditionalPath,
      {in: 'Call', at: 'arg'},
      {in: 'Record', at: 'rules'},
    ];

    const groupsExpr = Expressions.getIn(expr, pathToGroups);
    const groups = groupsExpr ? Expressions.getList(groupsExpr) : null;
    if (groups) {
      // remove the groups tagged for removal
      const updatedGroups = groups.filter(
        (_, index) => !groupsToRemove.includes(index)
      );

      // if all groups are removed, replace the conditional with its fallback
      if (updatedGroups.length === 0) {
        // unbind any references to the target field in the fallback value
        const fallbackValueWithUnboundReferences = unbindReferences(
          fallbackValue,
          fieldId,
          component,
          plugins
        );

        const lastConditionalPathPart =
          conditionalPath[conditionalPath.length - 1];
        const doesConditionalHaveWrappingCall = Boolean(
          lastConditionalPathPart &&
          lastConditionalPathPart.in === 'Call' &&
          lastConditionalPathPart.at === 'arg'
        );
        const pathToWrappingImplicit = conditionalPath.slice(0, -1);
        const wrappingImplicitReturnType = doesConditionalHaveWrappingCall
          ? getImplicitFunctionReturnType(expr, pathToWrappingImplicit, plugins)
          : null;

        if (wrappingImplicitReturnType) {
          // handle conditionals wrapped in an implicit function call
          if (!Expressions.isBinding(fallbackValueWithUnboundReferences)) {
            // if the fallback is a static value, replace the implicit function
            // call with a static value that has been run through the implicit
            // function
            return replaceImplicitFunctionValue(
              expr,
              wrappingImplicitReturnType,
              plugins,
              fallbackValueWithUnboundReferences,
              conditionalPath,
              pathToWrappingImplicit
            );
          }

          // the following logic is hyper-targeted towards the conditional
          // visibility scope of conditional settings where visibility
          // conditionals are boolean conditionals wrapped in a call to the
          // booleanToVisibilityConditions implicit function
          const fallbackCall = Expressions.getCall(
            fallbackValueWithUnboundReferences
          );
          if (
            fallbackCall &&
            isDynamoBooleanToUnionBooleanRef(fallbackCall.fun)
          ) {
            const wrappingCallExpr = Expressions.getIn(
              expr,
              pathToWrappingImplicit
            );
            const wrappingCall = wrappingCallExpr
              ? Expressions.getCall(wrappingCallExpr)
              : null;
            if (
              wrappingCall &&
              isBooleanToVisibilityConditionsRef(wrappingCall.fun)
            ) {
              // if the fallback is a dynamoBooleanToUnionBoolean call and the
              // conditional is wrapped in a booleanToVisibilityConditions call,
              // then we can replace the conditional and wrapping implicit with
              // the Dynamo binding from the fallback with the implicit function
              // changed to dynamoBooleanToVisibilityConditions
              return Expressions.setIn(
                expr,
                pathToWrappingImplicit,
                ECall({
                  fun: EVariable(
                    Variable.qualified(
                      qualify(
                        Plugins.name('DynamoGateway'),
                        typeName('dynamoBooleanToVisibilityConditions')
                      )
                    )
                  ),
                  arg: fallbackCall.arg,
                })
              );
            }
          }
        }

        expr = Expressions.setIn(
          expr,
          conditionalPath,
          fallbackValueWithUnboundReferences
        );
      } else {
        expr = Expressions.setIn(expr, pathToGroups, EList(updatedGroups));
      }
    }
  }

  return expr;
}

const isBooleanToVisibilityConditionsRef = matchExpression({
  Variable: Variable.equals(
    Variable.qualified(
      qualify(
        Plugins.name('Visibility'),
        typeName('booleanToVisibilityConditions')
      )
    )
  ),
  _: constantFalse,
});

const isDynamoBooleanToUnionBooleanRef = matchExpression({
  Variable: Variable.equals(
    Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        typeName('dynamoBooleanToUnionBoolean')
      )
    )
  ),
  _: constantFalse,
});

type DeconstructedConditional = {
  sourceFieldsByConditionAndGroup: Expression[][];
  fallbackValue: Expression;
};

function deconstructConditionalForUnbinding(
  conditionalExpr: Expression
): DeconstructedConditional | null {
  const call = Expressions.getCall(conditionalExpr);
  const config = call ? Expressions.getRecord(call.arg) : null;

  if (config) {
    const groups = config.rules ? Expressions.getList(config.rules) : null;
    const fallbackValueExpr = config.fallback;

    if (groups && fallbackValueExpr) {
      const sourceFieldsByConditionAndGroup: Expression[][] = [];
      for (const groupExpr of groups) {
        const sourceFieldsByCondition: Expression[] = [];
        sourceFieldsByConditionAndGroup.push(sourceFieldsByCondition);
        const group = Expressions.getRecord(groupExpr);

        if (!group) {
          return null;
        }

        const everyOrSomeCall = group.condition
          ? Expressions.getCall(group.condition)
          : null;
        const conditions = everyOrSomeCall
          ? Expressions.getList(everyOrSomeCall.arg)
          : null;

        if (!conditions) {
          return null;
        }

        for (const conditionExpr of conditions) {
          const applyPredicateCall = Expressions.deconstructCall(conditionExpr);
          const operandCallExpr = applyPredicateCall.args[0];
          const operandCall = operandCallExpr
            ? Expressions.getCall(operandCallExpr)
            : null;

          if (!operandCall) {
            return null;
          }

          sourceFieldsByCondition.push(operandCall.arg);
        }
      }

      return {
        sourceFieldsByConditionAndGroup,
        fallbackValue: fallbackValueExpr,
      };
    }
  }

  return null;
}

function reportEvaluationBug(functionExprToEvaluate: Expression | undefined) {
  reportBug(
    'ImplicitFunctionEvaluationError',
    'Failed to evaluate implicit function expression',
    {
      team: 'structure',
      ticket: 'https://webflow.atlassian.net/browse/STRUCT-1755',
      functionExprToEvaluate,
    }
  );
  throw new TypeError('Unable to delete component property');
}
